import { yupResolver } from '@hookform/resolvers/yup'
import { DevTool } from '@hookform/devtools'
import { Box, Divider, Grid, Stack, Typography } from '@mui/material'

import PropTypes from 'prop-types'
import { useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'

import { acGetAllApplicationServices, acPostApplicationService } from '../../store/actions'
import { PrimaryButton, UploadButton, Alert, InfoTitle } from '../default'

import { FormInputNumber, FormInputPercentage, FormInputText } from './formInputs'
import { FormValidationCreateApplicationService } from './formValidations'
import { SearchCompanyForm } from './SearchCompanyForm'

/**
 * @description Renders a form for creating an application service.
 * 
 * @author Maria Eduarda Franke
 * @param {Object} props - The props object containing onClose and setShowSnackbar functions.
 * @returns {JSX.Element} - The JSX element representing the CreateApplicationServiceForm.
 */
export const CreateApplicationServiceForm = ({ onClose, setShowSnackbar }) => {

    const defaultValues = {
        cnpj: '',
        companyName: '',
        target: null,
        gracePeriod: null,
        tolerableTicketsQty: null,
        tolerableTicketsValuePercentage: null,
        parserFileName: '',
        modelFileName: ''
    }

    const dispatch = useDispatch()

    const searchedCompany = useSelector(state => state.company.searchedCompany)

    const isCreatingApplicationService = useSelector(state => state.applicationServices.isCreatingApplicationService)
    const hasErrorCreatedApplicationService = useSelector(state => state.applicationServices.hasErrorCreatedApplicationService)

    const [parserFile, setParserFile] = useState(null)
    const [modelFile, setModelFile] = useState(null)

    const [validateError, setValidateError] = useState(null)

    const formProps = useForm({
        defaultValues: defaultValues,
        resolver: yupResolver(FormValidationCreateApplicationService)
    })

    const { control, setValue, handleSubmit, trigger, formState: { errors } } = formProps

    const onSubmit = handleSubmit((data) => {

        setValidateError(null)

        if (!validateFile(parserFile)) {
            setValidateError('Selecione um arquivo parser válido!')
            return
        }

        if (!validateFile(modelFile)) {
            setValidateError('Selecione um arquivo model válido!')
            return
        }

        const formData = new FormData()

        formData.append('parser', parserFile)
        formData.append('model', modelFile)

        formData.append('cnpj', searchedCompany.cnpj)
        formData.append('companyName', searchedCompany.name)

        formData.append('target', data.target)
        formData.append('gracePeriod', data.gracePeriod)

        if (data.tolerableTicketsQty) {
            formData.append('tolerableTicketsQty', data.tolerableTicketsQty)
        }
        if (data.tolerableTicketsValuePercentage) {
            const percentage = data.tolerableTicketsValuePercentage / 100
            formData.append('tolerableTicketsValuePercentage', percentage)
        }

        dispatch(acPostApplicationService(formData))
            .then(() => {
                onClose()
                setShowSnackbar(true)
                dispatch(acGetAllApplicationServices())
                    .catch(error => console.error(error))
            })
            .catch(error => console.error(error))
    })

    const handleParserUpload = event => {

        if (event.target.files.length > 0) {
            const file = event.target.files[0]
            setParserFile(file)
            setValue('parserFileName', file.name ?? 'parser.json')
            trigger('parserFileName')
        }
    }

    const handleModelUpload = event => {

        if (event.target.files.length > 0) {
            const file = event.target.files[0]
            setModelFile(file)
            setValue('modelFileName', file.name ?? 'model.mdl')
            trigger('modelFileName')
        }
    }

    const validateFile = file => {

        if (!file) {
            return false
        }

        if (!(file instanceof File)) {
            return false
        }

        return true
    }

    return (
        <FormProvider {...formProps}>

            <DevTool control={control} />

            <SearchCompanyForm />

            {searchedCompany && (
                <Box
                    component='form' onSubmit={onSubmit} noValidate
                    display='flex' flexDirection='column' gap={2} mt={2}
                >
                    <Divider />

                    <Typography fontWeight='bold'>
                        Upload de Arquivos
                    </Typography>

                    <Grid container alignItems='center' spacing={2}>
                        <Grid item flexGrow={1}>
                            <FormInputText
                                name='parserFileName'
                                label='Parser'
                                control={control}
                                errors={errors.parserFileName}
                                fullWidth
                                disabled
                                required
                            />
                        </Grid>
                        <Grid item xs='auto'>
                            <UploadButton
                                label='Parser'
                                accept='.json'
                                onUpload={handleParserUpload}
                                sx={{ width: 150 }}
                            />
                        </Grid>
                    </Grid>

                    <Grid container alignItems='center' spacing={2}>
                        <Grid item flexGrow={1}>
                            <FormInputText
                                name='modelFileName'
                                label='Modelo'
                                control={control}
                                errors={errors.modelFileName}
                                fullWidth
                                disabled
                                required
                            />
                        </Grid>
                        <Grid item xs='auto'>
                            <UploadButton
                                label='Modelo'
                                accept='.mdl'
                                onUpload={handleModelUpload}
                                sx={{ width: 150 }}
                            />
                        </Grid>
                    </Grid>

                    <Divider />

                    <Typography fontWeight='bold'>
                        Regras de Negócio
                    </Typography>

                    <Stack direction={'row'} gap={2}>

                        {/* - `target`: definição de inadimplência (dias de atraso).
                            - `gracePeriod`: dias de atraso tolerável.
                            - `tolerableTicketsQty`: quantidade de pedidos inadimplentes toleráveis.
                            - `tolerableTicketsValuePercentage`: percentual do montante atrasado tolerável. */}

                        <Stack gap={1} width='100%'>
                            <InfoTitle
                                title='Definição de inadimplência'
                                tooltip='Default criteria / target / dias de atraso.'
                            />
                            <FormInputNumber
                                name='target'
                                label=''
                                control={control}
                                errors={errors.target}
                                fullWidth
                                required
                            />
                        </Stack>

                        <Stack gap={1} width='100%'>
                            <InfoTitle
                                title='Dias de atraso tolerável'
                                tooltip='Grace period: boletos que atrasarem até X dias não serão considerados.'
                            />
                            <FormInputNumber
                                name='gracePeriod'
                                label=''
                                control={control}
                                errors={errors.gracePeriod}
                                fullWidth
                                required
                            />
                        </Stack>
                    </Stack>

                    <Stack direction={'row'} gap={2}>
                        <Stack gap={1} width='100%'>
                            <InfoTitle
                                title='Quantidade de pedidos atrasados toleráveis'
                                tooltip='Quantidade de pedidos que mesmo atrasados, serão perdoados.'
                                isRequired={false}
                            />
                            <FormInputNumber
                                name='tolerableTicketsQty'
                                label=''
                                control={control}
                                errors={errors.tolerableTicketsQty}
                                fullWidth
                            />
                        </Stack>

                        <Stack gap={1} width='100%'>
                            <InfoTitle
                                title='Percentual do valor atrasado tolerável'
                                tooltip='Percentual do somatório dos pedidos atrasados que será perdoado.'
                                isRequired={false}
                            />
                            <FormInputPercentage
                                name='tolerableTicketsValuePercentage'
                                label=''
                                control={control}
                                errors={errors.tolerableTicketsValuePercentage}
                                fullWidth
                            />
                        </Stack>
                    </Stack>

                    {validateError && (
                        <Alert severity='warning' message={validateError} />
                    )}

                    {hasErrorCreatedApplicationService && (
                        <Alert severity='error' message='Erro ao criar aplicação!' />
                    )}

                    <PrimaryButton
                        label='Criar Aplicação'
                        loading={isCreatingApplicationService}
                        type='submit'
                        name='submitButton'
                        fullWidth
                    />

                </Box>
            )}
        </FormProvider>
    )

}

CreateApplicationServiceForm.propTypes = {
    onClose: PropTypes.func.isRequired,
    setShowSnackbar: PropTypes.func.isRequired
}